/**
 * Perks Redux Slice
 * Manages perks state in the application
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Perk, UnlockedPerk, PerkReward } from '../../types/achievements';
import perksService from '../../services/perks.service';
import { RootState } from '../index';

interface PerksState {
  perks: Perk[];
  unlockedPerks: UnlockedPerk[];
  rewards: Record<string, PerkReward>;
  loading: boolean;
  error: string | null;
}

const initialState: PerksState = {
  perks: [],
  unlockedPerks: [],
  rewards: {},
  loading: false,
  error: null,
};

// Async thunks
export const fetchPerks = createAsyncThunk(
  'perks/fetchAll',
  async () => {
    return await perksService.getAllPerks();
  }
);

export const fetchUserPerks = createAsyncThunk(
  'perks/fetchUserPerks',
  async (uid: string) => {
    return await perksService.getUserUnlockedPerks(uid);
  }
);

export const unlockPerk = createAsyncThunk(
  'perks/unlock',
  async ({ uid, perkKey }: { uid: string; perkKey: string }) => {
    const success = await perksService.unlockPerk(uid, perkKey);
    if (!success) {
      throw new Error('Failed to unlock perk');
    }
    // Return the new unlocked perk
    return {
      perkKey,
      unlockedAt: Date.now(),
      hasSeenReward: false,
      copiedCode: false,
    } as UnlockedPerk;
  }
);

export const fetchPerkReward = createAsyncThunk(
  'perks/fetchReward',
  async (perkKey: string) => {
    const reward = await perksService.getPerkReward(perkKey);
    return { perkKey, reward };
  }
);

export const markRewardSeen = createAsyncThunk(
  'perks/markRewardSeen',
  async ({ uid, perkKey }: { uid: string; perkKey: string }) => {
    await perksService.markRewardSeen(uid, perkKey);
    return perkKey;
  }
);

export const markCodeCopied = createAsyncThunk(
  'perks/markCodeCopied',
  async ({ uid, perkKey }: { uid: string; perkKey: string }) => {
    await perksService.markCodeCopied(uid, perkKey);
    return perkKey;
  }
);

export const checkAndUnlock10For10 = createAsyncThunk(
  'perks/check10For10',
  async ({ uid, friendCount }: { uid: string; friendCount: number }) => {
    const unlocked = await perksService.check10For10Perk(uid, friendCount);
    if (unlocked) {
      return {
        perkKey: 'TEN_FOR_TEN',
        unlockedAt: Date.now(),
        hasSeenReward: false,
        copiedCode: false,
      } as UnlockedPerk;
    }
    return null;
  }
);

// Slice
const perksSlice = createSlice({
  name: 'perks',
  initialState,
  reducers: {
    setPerks: (state, action: PayloadAction<Perk[]>) => {
      state.perks = action.payload;
    },
    setUnlockedPerks: (state, action: PayloadAction<UnlockedPerk[]>) => {
      state.unlockedPerks = action.payload;
    },
    clearPerks: (state) => {
      state.perks = [];
      state.unlockedPerks = [];
      state.rewards = {};
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all perks
      .addCase(fetchPerks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPerks.fulfilled, (state, action) => {
        state.loading = false;
        state.perks = action.payload;
      })
      .addCase(fetchPerks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch perks';
      })
      // Fetch user perks
      .addCase(fetchUserPerks.fulfilled, (state, action) => {
        state.unlockedPerks = action.payload;
      })
      // Unlock perk
      .addCase(unlockPerk.fulfilled, (state, action) => {
        state.unlockedPerks.push(action.payload);
      })
      // Fetch reward
      .addCase(fetchPerkReward.fulfilled, (state, action) => {
        if (action.payload.reward) {
          state.rewards[action.payload.perkKey] = action.payload.reward;
        }
      })
      // Mark reward seen
      .addCase(markRewardSeen.fulfilled, (state, action) => {
        const perk = state.unlockedPerks.find(p => p.perkKey === action.payload);
        if (perk) {
          perk.hasSeenReward = true;
        }
      })
      // Mark code copied
      .addCase(markCodeCopied.fulfilled, (state, action) => {
        const perk = state.unlockedPerks.find(p => p.perkKey === action.payload);
        if (perk) {
          perk.copiedCode = true;
        }
      })
      // Check 10 for 10
      .addCase(checkAndUnlock10For10.fulfilled, (state, action) => {
        if (action.payload) {
          state.unlockedPerks.push(action.payload);
        }
      });
  },
});

// Actions
export const { setPerks, setUnlockedPerks, clearPerks } = perksSlice.actions;

// Selectors
export const selectPerks = (state: RootState) => state.perks.perks;
export const selectUnlockedPerks = (state: RootState) => state.perks.unlockedPerks;
export const selectPerkRewards = (state: RootState) => state.perks.rewards;
export const selectPerksLoading = (state: RootState) => state.perks.loading;
export const selectPerksError = (state: RootState) => state.perks.error;

export const selectPerkById = (perkKey: string) => (state: RootState) =>
  state.perks.perks.find(p => p.key === perkKey);

export const selectIsPerkUnlocked = (perkKey: string) => (state: RootState) =>
  state.perks.unlockedPerks.some(p => p.perkKey === perkKey);

export const selectPerkReward = (perkKey: string) => (state: RootState) =>
  state.perks.rewards[perkKey];

export default perksSlice.reducer;